import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputLabel, MenuItem, Select, TextField, ThemeProvider, createTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { ChangeUser, User } from "../../models/AdminUsersModels";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import { changeUser } from "../../adminUsersSlice";
import adminRolesApi from "../../api/adminRolesApi";
import { Role } from "../../models/AdminRolesModels";
import { useRoleLabel } from "../../../customer/settings/utils/useRoleLabel";

export interface UserDialogProps {
  open: boolean;
  onClose: () => void;
  user: User;
}

type FormValues = {
  userName: string;
  firstName: string;
  lastName: string;
  roleId: string;
};

const UserDialog = ({ open, onClose, user }: UserDialogProps) => {
  const dispatch = useAppDispatch();
  const getRoleLabel = useRoleLabel();
  const [roles, setRoles] = useState<Role[]>([]);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const [data, setData] = useState<FormValues>({ userName: "", firstName: "", lastName: "", roleId: "" });

  const onSubmit: SubmitHandler<FormValues> = (data: FormValues) => {
    setData(data);
    handlerChangeUser(data)
  };

  const handlerChangeUser = async (model: ChangeUser) => {
    if (user) {
      await dispatch(changeUser({ id: user.id, model }));
      onClose();
    }
  }

  const loadRoles = async () => {
    const roles = await adminRolesApi.getRoles();
    setRoles(roles);
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const theme = createTheme({
    components: {
      MuiDialog: {
        styleOverrides: {
          root: {
            ".MuiDialogContent-root": {
              padding: "0 24px 20px",
            },

            ".MuiDialogTitle-root": {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            },

            ".MuiPaper-root": {
              width: "600px",
              display: "flex",
              justifyContent: "center",
            },
          },
        },
      },
    },
  });
  return (
    <>
      <ThemeProvider theme={theme}>
        <Dialog open={open} onClose={onClose}>
          <DialogTitle>
            <p className="heading-2">Edit user details</p>
          </DialogTitle>

          <IconButton
            aria-label="close"
            onClick={onClose}

            sx={{
              position: "absolute",
              right: 14,
              top: 14,
              color: "#333333",
            }}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent>
            {user &&
              <form id="userForm" onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <InputLabel required>First name</InputLabel>
                  <TextField
                    className="w-full"
                    defaultValue={user?.profile?.firstName}
                    {...register("firstName", { required: true })}
                    error={!!errors.firstName}
                    helperText={errors.firstName ? "Field is required" : ""}
                  />
                </div>

                <div className="mt-4">
                  <InputLabel required>Last name</InputLabel>
                  <TextField
                    className="w-full"
                    defaultValue={user?.profile?.lastName}
                    {...register("lastName", { required: true })}
                    error={!!errors.lastName}
                    helperText={errors.lastName ? "Field is required" : ""}
                  />
                </div>

                <div className="mt-4">
                  <InputLabel>Role</InputLabel>
                  <Select
                    id="role"
                    className="w-full"
                    defaultValue={user?.role?.id}
                    {...register("roleId", { required: true })}
                  >
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {getRoleLabel(role)}
                      </MenuItem>
                    ))}
                  </Select>
                </div>

                <div className="mt-4">
                  <InputLabel>Email Address</InputLabel>
                  <TextField
                    className="w-full"
                    defaultValue={user?.userName}
                    {...register("userName", { disabled: true })}
                  />
                </div>
              </form>}
          </DialogContent>

          <DialogActions className="flex">
            <Button variant="text" className="w-full" onClick={onClose}>
              Cancel
            </Button>

            <Button form="userForm" type="submit" variant="contained" className="w-full">
              Save changes
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </>
  );
};

export default UserDialog;
